import React, { useEffect, useState } from 'react';
import { useGlobalLoader } from 'contexts/GlobalLoaderContext';
import { getLoadDetails } from 'apis/adminServices/dashboardService';
import { LoadDetail, LoadDetailsResponse } from './types';
import { Link } from 'react-router-dom';
import SearchBar from '../creditsManagement/searchbar';
import LoadFilter from './loadFilter';

const LoadPosted: React.FC = () => {
    const [loadDetails, setLoadDetails] = useState<LoadDetail[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [fromDate, setFromDate] = useState<string | undefined>(undefined);
    const [toDate, setToDate] = useState<string | undefined>(undefined);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const { showLoader, hideLoader } = useGlobalLoader();

    const isSearching = searchTerm.trim() !== "";

    // Reset pagination when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const fetchLoadDetails = async (page: number, size: number) => {
        try {
            setLoading(true);
            showLoader();

            const response: LoadDetailsResponse = await getLoadDetails(
                page,
                size,
                searchTerm || undefined,
                fromDate,
                toDate
            );

            if (!response || !response.items) {
                setLoadDetails([]);
                setTotalItems(0);
            } else {
                setLoadDetails(response.items.sort((a,b) => new Date(b.requestedDate).getTime() - new Date(a.requestedDate).getTime()));
                setTotalItems(response.totalCount);
            }
        } catch (error) {
            console.error('Error fetching load details:', error);
            setLoadDetails([]);
            setTotalItems(0);
        } finally {
            hideLoader();
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLoadDetails(currentPage, pageSize);
    }, [currentPage, pageSize, searchTerm, fromDate, toDate]);

    const handleApplyFilters = (filters: { fromDate?: string; toDate?: string }) => {
        setFromDate(filters.fromDate);
        setToDate(filters.toDate);
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setFromDate(undefined);
        setToDate(undefined);
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalItems / pageSize);

    const generatePageNumbers = () => {
        const pages: number[] = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

        if (endPage === totalPages) {
            startPage = Math.max(1, totalPages - maxPagesToShow + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    };

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString('en-GB');
    };

    const formatDateTime = (dateString: string): string => {
        const date = new Date(dateString);
        return date
            .toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
            })
            .replace(',', '');
    };

    return (
        <div>
            <section className="flex justify-between">
                <p className="text-grease-100 text-lg font-semibold pl-8">Loads Posted</p>
                <nav className="flex items-center gap-2">
                    <span className="st-dashboard text-lightgray-200 text-xs" aria-hidden="true" />
                    <Link className="text-lightgray-200 text-sm" to="/admin/dashboard">Dashboard</Link>
                    <span className="st-arrow-right text-lightgray-200 text-xs" aria-hidden="true" />
                    <span className="text-royalblue-100 text-sm">Loads Posted</span>
                </nav>
            </section>

            <div className="px-6 pt-6">
                <section>
                    <div className="bg-white rounded-xl p-4 mt-2">
                        <div className="flex flex-wrap gap-4 items-center justify-between pb-6">
                            <p className="text-grease-100 text-sm font-semibold">Load Details</p>

                            <div className="flex flex-wrap items-center gap-3 relative">
                                <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

                                <div className="relative">
                                    <button
                                        id="dropdownDefaultButton-loadPosted"
                                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                                        className="flex gap-2 p-3 border bg-violet-200 rounded-lg"
                                    >
                                        <i className="st-filter text-white"></i>
                                        <p className="font-medium text-white text-sm">Filters</p>
                                    </button>

                                    <LoadFilter
                                        isOpen={isFilterOpen}
                                        onClose={() => setIsFilterOpen(false)}
                                        onApply={handleApplyFilters}
                                        onClear={handleClearFilters}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="relative overflow-x-auto sm:rounded-lg">
                            <table className="w-full text-sm text-left text-gray-500">
                                <thead className="text-xs text-gray-700 p-4 bg-blue-50">
                                    <tr>
                                        <th className="px-6 py-4 rounded-l-lg">
                                            <p className="text-lightgray-200">S.No.</p>
                                        </th>
                                        <th className="px-6 py-4">
                                            <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">Requested By</p>
                                        </th>
                                        <th className="px-6 py-4">
                                            <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">Requested Date</p>
                                        </th>
                                        <th className="px-6 py-4">
                                            <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">Flight Number</p>
                                        </th>
                                        <th className="px-6 py-4">
                                            <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">Flight Date & Time</p>
                                        </th>
                                        <th className="px-6 py-4">
                                            <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">Tokens Received</p>
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={6} className="text-center py-4">Loading...</td>
                                        </tr>
                                    ) : loadDetails.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="text-center py-4">No data found</td>
                                        </tr>
                                    ) : (
                                        loadDetails.map((detail, index) => (
                                            <tr key={index} className="odd:bg-white even:bg-blue-50 border">
                                                <td className="px-6 py-4 text-grease-100">
                                                    <p>{isSearching ? index + 1 : (currentPage - 1) * pageSize + index + 1}</p>
                                                </td>

                                                <td className="px-6 py-4 text-grease-100">
                                                    <p className="px-4">{detail.requestedBy}</p>
                                                </td>

                                                <td className="px-6 py-4 text-grease-100">
                                                    <p className="px-4">{formatDate(detail.requestedDate)}</p>
                                                </td>

                                                <td className="px-10 py-4 text-grease-100">
                                                    <p className="px-4">{detail.flightNumber}</p>
                                                </td>

                                                <td className="px-6 py-4 text-grease-100">
                                                    <p className="px-4">{formatDateTime(detail.flightDate)}</p>
                                                </td>

                                                <td className="px-16 py-4 text-grease-100">
                                                    <span className="px-4 inline-block min-w-[50px] text-center">
                                                        {detail.creditSpent}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
                                <div className="flex items-center gap-5">
                                    <p className="text-lightgray-200 text-sm">Rows per page</p>
                                    <form className="w-fit">
                                        <select
                                            className="block w-full border-none pl-3 pr-10 py-2 rounded-lg bg-lightblue-100 text-sm text-grease-100"
                                            value={pageSize}
                                            onChange={(e) => {
                                                setPageSize(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                        >
                                            <option value={7}>7</option>
                                            <option value={8}>8</option>
                                            <option value={9}>9</option>
                                            <option value={10}>10</option>
                                        </select>
                                    </form>
                                </div>

                                <ul className="flex flex-wrap items-center gap-3 my-6 text-sm">
                                    {/* Previous */}
                                    <li>
                                        <button
                                            disabled={currentPage === 1}
                                            className={`w-7 h-7 flex items-center justify-center border rounded-md 
                                                ${currentPage === 1 ? "text-gray-300 border-gray-300" : "text-primaryblue-50 border-primaryblue-50"}
                                            `}
                                            onClick={() => setCurrentPage(currentPage - 1)}
                                        >
                                            <i className="st-arrow-right rotate-180 text-xl"></i>
                                        </button>
                                    </li>

                                    {/* Page Numbers */}
                                    {generatePageNumbers().map((pageNum) => (
                                        <li key={pageNum}>
                                            <button
                                                className={`w-7 h-7 flex items-center justify-center rounded-md 
                                                    ${currentPage === pageNum
                                                        ? "bg-primaryblue-50 text-white border border-primaryblue-50"
                                                        : "text-primaryblue-50 border border-primaryblue-50"
                                                    }
                                                `}
                                                onClick={() => setCurrentPage(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        </li>
                                    ))}

                                    {/* Next */}
                                    <li>
                                        <button
                                            disabled={currentPage === totalPages}
                                            className={`w-7 h-7 flex items-center justify-center border rounded-md 
                                                ${currentPage === totalPages ? "text-gray-300 border-gray-300" : "text-primaryblue-50 border-primaryblue-50"}
                                            `}
                                            onClick={() => setCurrentPage(currentPage + 1)}
                                        >
                                            <i className="st-arrow-right text-xl"></i>
                                        </button>
                                    </li>
                                </ul>
                            </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default LoadPosted;
